import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Truck, 
  Search, 
  Calendar, 
  Filter,
  Download,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { formatDate } from '../../utils/format';
import { ShippingStatus } from '../../types/orders';
import { fetchWithAuth } from '../../hooks/useAuth';

const SHIPPING_STATUSES = ['REGISTERED', 'IN_TRANSIT', 'DELIVERED', 'CANCELED', 'FAILED', 'RETURNED', 'PENDING'] as const;

const shippingStatusColors: Record<ShippingStatus, string> = {
  REGISTERED: 'bg-blue-100 text-blue-800',
  IN_TRANSIT: 'bg-yellow-100 text-yellow-800',
  DELIVERED: 'bg-green-100 text-green-800',
  CANCELED: 'bg-red-100 text-red-800',
  FAILED: 'bg-red-100 text-red-800',
  RETURNED: 'bg-purple-100 text-purple-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
};

interface ShipmentFilters {
  search?: string;
  status?: ShippingStatus;
  dateFrom?: string;
  dateTo?: string;
}

interface Shipment {
  id: number;
  orderId: number;
  awbNumber: string;
  awbUrl: string;
  status: ShippingStatus;
  customerName: string;
  customerAddress: string;
  createdAt: string;
  updatedAt: string;
}

interface ShipmentsResponse {
  shipments: Shipment[];
  total: number;
  page: number;
  pageSize: number;
}

export function ShippingPage() {
  const [filters, setFilters] = React.useState<ShipmentFilters>({
    search: '',
    status: undefined,
    dateFrom: '',
    dateTo: '',
  });

  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(10);

  // Fetch shipments
  const { data: shipmentsResponse, isLoading } = useQuery<ShipmentsResponse>({
    queryKey: ['shipments', filters, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      params.append('page', page.toString());
      params.append('limit', pageSize.toString());

      const response = await fetchWithAuth(`/api/admin/shipping?${params}`);
      if (!response.ok) throw new Error('Eroare la încărcarea expedierilor');
      return response.json();
    }
  });

  // Fetch service status
  const { data: serviceStatus } = useQuery({
    queryKey: ['shipping-status'],
    queryFn: async () => {
      const response = await fetchWithAuth('/api/admin/shipping/status');
      if (!response.ok) throw new Error('Eroare la verificarea stării serviciului');
      return response.json();
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <Truck className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Expedieri</h1>
            {serviceStatus && (
              <div className="flex items-center gap-2 mt-1">
                {serviceStatus.stare === 'operațional' ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : serviceStatus.stare === 'performanță redusă' ? (
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-600" />
                )}
                <p className={`text-sm ${
                  serviceStatus.stare === 'operațional' ? 'text-green-600' :
                  serviceStatus.stare === 'performanță redusă' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  Sameday: {serviceStatus.mesaj}
                </p>
              </div>
            )}
          </div>
        </div>
        <Button asChild className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
          <a href="https://sameday.ro" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4" />
            Sameday Portal
          </a>
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="font-semibold text-gray-900">Filtre</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              placeholder="Caută după AWB, client..."
              value={filters.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setFilters(f => ({ ...f, search: e.target.value }))
              }
            />
          </div>
          
          <Select
            value={filters.status ?? 'ALL'}
            onValueChange={(value) => 
              setFilters(f => ({ ...f, status: value === 'ALL' ? undefined : value as ShippingStatus }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Toate statusurile" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toate statusurile</SelectItem>
              {SHIPPING_STATUSES.map(status => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              type="date"
              value={filters.dateFrom}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setFilters(f => ({ ...f, dateFrom: e.target.value }))
              }
            />
          </div>

          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              type="date"
              value={filters.dateTo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setFilters(f => ({ ...f, dateTo: e.target.value }))
              }
            />
          </div>
        </div>
      </Card>

      {/* Shipments Table */}
      <Card className="shadow-sm hover:shadow-md transition-shadow duration-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>AWB</TableHead>
              <TableHead>Comandă</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Adresă</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Acțiuni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10">
                  Se încarcă...
                </TableCell>
              </TableRow>
            ) : shipmentsResponse?.shipments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10">
                  Nu există expedieri
                </TableCell>
              </TableRow>
            ) : (
              shipmentsResponse?.shipments.map((shipment) => (
                <TableRow key={shipment.id}>
                  <TableCell>{shipment.awbNumber}</TableCell>
                  <TableCell>#{shipment.orderId}</TableCell>
                  <TableCell>{shipment.customerName}</TableCell>
                  <TableCell className="max-w-xs truncate" title={shipment.customerAddress}>
                    {shipment.customerAddress}
                  </TableCell>
                  <TableCell>
                    <Badge className={`px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset ${
                      shippingStatusColors[shipment.status]} ring-current/20`}>
                      {shipment.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(shipment.createdAt)}</TableCell>
                  <TableCell>
                    <Button
                      variant="link"
                      className="flex items-center gap-1"
                      onClick={() => window.open(shipment.awbUrl, '_blank')}
                    >
                      <Download className="w-4 h-4" />
                      Descarcă AWB
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {shipmentsResponse && shipmentsResponse.total > pageSize && (
        <div className="flex justify-center mt-4">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex items-center gap-2 hover:bg-gray-100 transition-colors duration-200"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600">
                Pagina {page} din {Math.ceil(shipmentsResponse.total / pageSize)}
              </span>
            </div>
            <Button
              variant="outline"
              className="flex items-center gap-2 hover:bg-gray-100 transition-colors duration-200"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(shipmentsResponse.total / pageSize)}
            >
              Următor
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
